import { useCallback, useState } from 'react';

import { ORDER, PaginationParams } from '@repo/types';

export type PaginationHookPayload = {
  nextPage: () => void;
  prevPage: () => void;
  setPage: (page: number) => void;
  setTake: (take: number) => void;
  setOrder: (order: ORDER) => void;
  setOrderBy: (orderBy: string) => void;
  handleSearch: (text: string) => void;
  pagination: PaginationParams;
  search?: string;
};

type PaginationHookReturnParams = [PaginationHookPayload, () => void];

export type PaginationHookParams = {
  initialPage: number;
  take: number;
  initialOrder: ORDER;
  initialOrderByColumn: string;
};

const defaultParams: PaginationHookParams = {
  initialPage: 1,
  take: 10,
  initialOrder: ORDER.DESC,
  initialOrderByColumn: '',
} as const;

export const usePagination = (
  params?: PaginationHookParams,
): PaginationHookReturnParams => {
  const { initialPage, take, initialOrder, initialOrderByColumn } =
    params || defaultParams;

  const [currPage, setCurrPage] = useState(initialPage);
  const [currTake, setCurrTake] = useState(take);
  const [search, setSearch] = useState('');
  const [currOrder, setCurrOrder] = useState(initialOrder);
  const [currOrderByColumn, setCurrOrderByColumn] = useState(
    initialOrderByColumn || '',
  );

  const handleSearch = useCallback((text: string) => {
    setSearch(text);
  }, []);

  const reset = useCallback(() => {
    setCurrPage(1);
  }, []);

  const nextPage = useCallback(() => {
    setCurrPage((oldState) => {
      return oldState + 1;
    });
  }, []);

  const prevPage = useCallback(() => {
    setCurrPage((oldState) => {
      return oldState - 1;
    });
  }, []);

  const setPage = useCallback((page: number) => {
    setCurrPage(page);
  }, []);

  const setTake = useCallback((take: number) => {
    setCurrTake(take);
  }, []);

  const setOrder = useCallback((order: ORDER) => {
    setCurrOrder(order);
  }, []);

  const setOrderBy = useCallback((orderBy: string) => {
    setCurrOrderByColumn(orderBy);
  }, []);

  return [
    {
      nextPage,
      prevPage,
      setPage,
      setTake,
      setOrder,
      setOrderBy,
      pagination: {
        page: currPage,
        take: currTake,
        order: currOrder,
        orderByColumn: currOrderByColumn,
      },
      search,
      handleSearch,
    },
    reset,
  ];
};
